import React, { useState } from "react";
import { api } from "@/lib/api";
import { usePaywallGuard } from "@/lib/usePaywallGuard";
import { makeKey, getCache, setCache } from "@/lib/offlineCache";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DatePicker from "@/components/DatePicker";
import OfflineBadge from "@/components/OfflineBadge";
import { Grid3X3, Sparkles, Target, AlertTriangle, CheckCircle2 } from "lucide-react";

const VERDICT_COLORS = {
  complete: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  strong: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  partial: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  weak: "bg-rose-500/10 text-rose-300 border-rose-500/30",
};

export default function LoShuGrid() {
  const guard = usePaywallGuard();
  const [form, setForm] = useState({ dob: "", name: "" });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cachedAt, setCachedAt] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setReport(null);
    setCachedAt(null);
    const cacheKey = makeKey("lo-shu-grid", form);
    const cached = getCache(cacheKey);
    if (cached) { setReport(cached.data); setCachedAt(cached.cachedAt); }
    try {
      const data = await guard(
        async () => (await api.post("/numerology/lo-shu-grid", form)).data,
        "Lo Shu Grid"
      );
      setReport(data);
      setCachedAt(null);
      setCache(cacheKey, data);
    } catch (err) {
      if (!cached) setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <header className="space-y-1">
        <h1 className="font-serif text-3xl md:text-4xl text-slate-50">Lo Shu Grid</h1>
        <p className="text-slate-400 text-sm">The 3×3 Saturn box — see your missing numbers, planes & remedies at a glance.</p>
      </header>

      <Card className="glass border-white/10 p-6">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-slate-300">Date of Birth</Label>
            <div className="mt-1.5">
              <DatePicker
                value={form.dob}
                onChange={(v) => setForm({ ...form, dob: v })}
                testId="loshu-dob-input"
                placeholder="Pick your date of birth"
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Name (optional)</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="loshu-name-input"
              placeholder="Your full name"
              className="mt-1.5 bg-slate-900/50 border-white/10 text-white"
            />
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              disabled={loading || !form.dob}
              data-testid="loshu-submit-btn"
              className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium gap-2"
            >
              <Grid3X3 className="w-4 h-4" /> {loading ? "Generating..." : "Generate Grid"}
            </Button>
          </div>
        </form>
        {error && <div className="mt-3 text-sm text-rose-400" data-testid="loshu-error">{error}</div>}
      </Card>

      {report && (
        <div className="space-y-6 animate-fade-up" data-testid="loshu-report">
          {cachedAt && (
            <div className="flex justify-end">
              <OfflineBadge cachedAt={cachedAt} testId="loshu-offline-badge" />
            </div>
          )}

          {/* Overall + Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Card className="glass border-white/10 p-5 lg:col-span-1">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Your 3×3 Grid</div>
              <div className="grid grid-cols-3 gap-2.5">
                {report.grid.map((row, ri) => row.map((cell, ci) => {
                  const filled = cell.count > 0;
                  return (
                    <div
                      key={`${ri}-${ci}`}
                      data-testid={`loshu-cell-${cell.number}`}
                      className={`aspect-square rounded-xl border flex flex-col items-center justify-center transition ${
                        filled
                          ? "bg-amber-500/15 border-amber-500/40 text-amber-200"
                          : "bg-white/[0.02] border-white/5 text-slate-600"
                      }`}
                    >
                      <div className="font-serif text-3xl">{cell.number}</div>
                      <div className="text-[10px] uppercase tracking-wider mt-1">
                        {filled ? `${cell.count}×` : "missing"}
                      </div>
                    </div>
                  );
                }))}
              </div>
              <div className="mt-5 text-sm text-slate-300">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Overall completeness</span>
                  <span className="text-amber-300 font-semibold" data-testid="loshu-score">{report.overall.score}%</span>
                </div>
                <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{report.overall.summary}</div>
              </div>
            </Card>

            <Card className="glass border-white/10 p-5 lg:col-span-2">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
                <Target className="w-3.5 h-3.5" /> Planes & axes
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {report.planes.map((p) => (
                  <div key={p.key} className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={`plane-${p.key}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-sm text-slate-100 font-medium">{p.name}</div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${VERDICT_COLORS[p.verdict]}`}>
                        {p.verdict.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mb-2">Cells: {p.cells.join(" · ")}</div>
                    <div className="text-xs text-slate-400 leading-relaxed">{p.note}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Driver & Conductor compact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="glass border-white/10 p-5">
              <div className="text-xs uppercase tracking-wider text-amber-300/80 mb-2">Driver · Mooladhar</div>
              <div className="flex items-baseline gap-3">
                <div className="font-serif text-5xl text-gold" data-testid="loshu-driver">{report.driver.number}</div>
                <div>
                  <div className="text-sm text-slate-200">{report.driver.planet}</div>
                  <div className="text-xs text-slate-400">{report.driver.trait}</div>
                </div>
              </div>
            </Card>
            <Card className="glass border-white/10 p-5">
              <div className="text-xs uppercase tracking-wider text-rose-300/80 mb-2">Conductor · Bhagyank</div>
              <div className="flex items-baseline gap-3">
                <div className="font-serif text-5xl text-rose-300" data-testid="loshu-conductor">{report.conductor.number}</div>
                <div>
                  <div className="text-sm text-slate-200">{report.conductor.planet}</div>
                  <div className="text-xs text-slate-400">{report.conductor.trait}</div>
                </div>
              </div>
            </Card>
          </div>

          {/* Insights */}
          {report.insights.length > 0 && (
            <Card className="glass border-white/10 p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> What it means for you
              </div>
              <div className="space-y-3">
                {report.insights.map((ins, i) => (
                  <div
                    key={i}
                    data-testid={`insight-${ins.type}-${ins.number}`}
                    className="rounded-xl bg-white/[0.02] border border-white/5 p-3.5"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {ins.type === "missing"
                        ? <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
                        : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                      <div className="text-sm text-slate-100 font-medium">{ins.headline}</div>
                    </div>
                    <div className="text-xs text-slate-400 leading-relaxed">{ins.body}</div>
                    {ins.remedy && (
                      <div className="mt-2 text-xs text-amber-300/85 leading-relaxed">
                        <span className="text-amber-300 font-semibold">Remedy: </span>{ins.remedy}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
